<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="720" scrollable>
    <v-card>
      <v-card-title class="text-h6 font-weight-bold py-4 px-6" style="font-family: Montserrat !important;">
        <v-icon left color="primary">mdi-replay</v-icon>
        Devolución
        <span v-if="pago && pago.identificador" class="ml-2 grey--text text--darken-1">
          · Pago #{{ pago.identificador }}
        </span>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-5">
        <v-alert
          v-if="!lastResult && !errorMsg"
          type="info"
          dense
          text
          border="left"
          class="mb-4 devolucion-legend"
          icon="mdi-information-outline"
        >
          La devolución se aplica a un <b>pagointento</b> (intento de cobro) puntual, no al pago completo.
          Si el pago tiene un solo intento cobrado, se devuelve automáticamente.
          Si tiene varios, el sistema te mostrará la lista para elegir el intento a devolver.
        </v-alert>

        <v-alert
          v-if="errorMsg"
          type="error"
          dense
          text
          class="mb-4"
          dismissible
          @input="errorMsg = null"
        >
          {{ errorMsg }}
        </v-alert>

        <div v-if="lastResult" class="success-card pa-6 mb-2 text-center">
          <v-avatar size="80" color="success" class="success-avatar mb-3">
            <v-icon size="52" color="white">mdi-check-bold</v-icon>
          </v-avatar>
          <h3 class="success-title">¡Devolución realizada con éxito!</h3>
          <p class="success-subtitle mb-4" v-if="lastResult.canal">
            Procesada por el canal <strong>{{ lastResult.canal }}</strong>
          </p>

          <v-divider class="mb-4" />

          <div class="result-grid text-left">
            <div class="result-item" v-if="resultField('importe') != null">
              <div class="result-label">Importe devuelto</div>
              <div class="result-value result-value--lg">
                {{ formatPesos(resultField('importe')) }}
              </div>
            </div>
            <div class="result-item" v-if="resultField('external_id')">
              <div class="result-label">ID externo</div>
              <div class="result-value">{{ resultField('external_id') }}</div>
            </div>
            <div class="result-item" v-if="resultField('moneda')">
              <div class="result-label">Moneda</div>
              <div class="result-value">{{ resultField('moneda') }}</div>
            </div>
            <div class="result-item" v-if="resultField('pagointento_id')">
              <div class="result-label">ID intento devuelto</div>
              <div class="result-value">#{{ resultField('pagointento_id') }}</div>
            </div>
          </div>
        </div>

        <!-- Info del pago -->
        <v-row dense v-if="pago && !lastResult">
          <v-col cols="12" sm="6">
            <p class="mb-1"><strong>Importe:</strong> {{ formatMoney(pago.amount) }}</p>
          </v-col>
          <v-col cols="12" sm="6">
            <p class="mb-1"><strong>Canal:</strong> {{ pago.nombre_channel || "-" }}</p>
          </v-col>
          <v-col cols="12" sm="6">
            <p class="mb-1"><strong>Pagador:</strong> {{ pago.payer_name || "-" }}</p>
          </v-col>
          <v-col cols="12" sm="6">
            <p class="mb-1"><strong>Referencia:</strong> {{ pago.external_reference || "-" }}</p>
          </v-col>
        </v-row>

        <!-- Paso 1: selección (cuando requiere_seleccion) -->
        <template v-if="requiereSeleccion && !lastResult">
          <v-divider class="my-4" />
          <p class="mb-3">
            Este pago tiene varios pagointentos cobrados. Elegí cuál devolver:
          </p>

          <v-data-table
            :headers="headers"
            :items="requiereSeleccion"
            :items-per-page="-1"
            hide-default-footer
            show-select
            single-select
            item-key="pagointento_id"
            v-model="selected"
            :loading="loading"
            no-data-text="Sin pagointentos"
            class="intentos-table"
          >
            <template v-slot:[`item.state_comment`]="{ item }">
              <v-chip small :color="stateColor(item.state_comment)" dark>
                {{ item.state_comment }}
              </v-chip>
            </template>
            <template v-slot:[`item.paid_at`]="{ item }">
              {{ item.paid_at ? formatDate(item.paid_at) : "-" }}
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              {{ formatMoney(item.amount) }}
            </template>
            <template v-slot:[`item.channel`]="{ item }">
              <v-chip small :color="channelColor(item.channel)" dark>
                {{ item.channel }}
              </v-chip>
            </template>
            <template v-slot:[`item.devolucion_at`]="{ item }">
              <v-chip v-if="item.devolucion_at" small color="warning" dark>
                {{ formatDate(item.devolucion_at) }}
              </v-chip>
              <span v-else class="caption grey--text">—</span>
            </template>
          </v-data-table>
        </template>

        <!-- Paso 0 / 2: form de devolución -->
        <template v-if="!requiereSeleccion && !lastResult">
          <v-divider class="my-4" />

          <v-radio-group v-model="refundType" row class="mt-0 mb-2">
            <v-radio label="Devolución total" value="total" />
            <v-radio label="Devolución parcial" value="parcial" />
          </v-radio-group>

          <FormattedInputGenerico
            v-if="refundType === 'parcial'"
            :value="amount"
            @input="
              (v) => {
                amount = v != null && v !== '' ? parseInt(v, 10) : null;
              }
            "
            label="Monto a devolver *"
            :persistent="false"
            :rules="amountRules"
            :hint="''"
          />
          <p
            v-if="pago && refundType === 'parcial'"
            class="caption grey--text mt-2 mb-0"
          >
            Máximo a devolver: <b>{{ formatMoney(pago.amount) }}</b>
          </p>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 pb-4">
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">Cerrar</v-btn>
        <v-btn
          v-if="!lastResult"
          color="primary"
          :loading="loading"
          :disabled="!isFormValid"
          @click="submit"
        >
          <v-icon left>mdi-check</v-icon>
          {{ requiereSeleccion ? "Confirmar devolución" : "Solicitar devolución" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Devolucion } from "@/services/devolucion.js";
import FormattedInputGenerico from "@/views/Dasboard/FormattedInputGenerico.vue";

export default {
  name: "DevolucionModal",
  components: { FormattedInputGenerico },
  props: {
    dialog: { type: Boolean, default: false },
    pago: { type: Object, default: null },
  },
  data: () => ({
    refundType: "total",
    amount: null,

    requiereSeleccion: null,
    selected: [],

    lastResult: null,
    errorMsg: null,
    loading: false,

    headers: [
      { text: "ID", value: "pagointento_id", width: 80 },
      { text: "External ID", value: "external_id" },
      { text: "Estado", value: "state_comment", width: 130 },
      { text: "Pagado", value: "paid_at", width: 170 },
      { text: "Monto", value: "amount", width: 130 },
      { text: "Canal", value: "channel", width: 130 },
      { text: "Devuelto", value: "devolucion_at", width: 170 },
    ],
  }),

  computed: {
    isFormValid() {
      if (!this.pago || !this.pago.identificador) return false;
      if (this.requiereSeleccion) {
        return this.selected.length > 0;
      }
      if (this.refundType === "parcial") {
        return this.amount > 0 && this.amount <= (this.pago.amount || Infinity);
      }
      return true;
    },

    amountRules() {
      if (this.refundType !== "parcial") return [];
      return [
        (v) => {
          const cents = parseInt(String(v || "").replace(/[^\d]/g, ""), 10) || 0;
          if (cents <= 0) return "Ingresá un monto mayor a 0";
          if (this.pago && this.pago.amount && cents > this.pago.amount) {
            return `El máximo a devolver es ${this.formatMoney(this.pago.amount)}`;
          }
          return true;
        },
      ];
    },
  },

  watch: {
    dialog(v) {
      if (v) this.resetState();
    },
    refundType(v) {
      if (v === "total") this.amount = null;
    },
  },

  methods: {
    resetState() {
      this.refundType = "total";
      this.amount = null;
      this.requiereSeleccion = null;
      this.selected = [];
      this.lastResult = null;
      this.errorMsg = null;
    },

    close() {
      this.$emit("update:dialog", false);
    },

    formatMoney(cents) {
      if (cents == null) return "-";
      const n = Number(cents) / 100;
      return n.toLocaleString("es-ar", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      });
    },

    formatPesos(pesos) {
      if (pesos == null || pesos === "") return "-";
      const n = Number(pesos);
      if (isNaN(n)) return "-";
      return n.toLocaleString("es-ar", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      });
    },

    resultField(key) {
      return this.lastResult && this.lastResult.data
        ? this.lastResult.data[key]
        : null;
    },

    formatDate(d) {
      if (!d) return "-";
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      return date.toLocaleString("es-AR", {
        dateStyle: "short",
        timeStyle: "short",
      });
    },

    channelColor(channel) {
      const c = String(channel || "").toUpperCase();
      if (c.includes("CREDIT") || c.includes("DEBIT")) return "info";
      if (c.includes("QR")) return "success";
      return "grey";
    },

    stateColor(state) {
      const s = String(state || "").toUpperCase();
      if (s.includes("APROB")) return "success";
      if (s.includes("RECH")) return "error";
      return "info";
    },

    buildPayload() {
      const body = { pago_id: this.pago.identificador };
      if (this.requiereSeleccion && this.selected.length) {
        body.pagointento_id = this.selected[0].pagointento_id;
      }
      if (this.refundType === "parcial" && this.amount > 0) {
        body.amount = this.amount;
      }
      return body;
    },

    async submit() {
      if (!this.isFormValid) return;
      this.errorMsg = null;
      this.loading = true;
      try {
        const { data } = await Devolucion.devolver(this.buildPayload());
        if (data && data.status === "requiere_seleccion") {
          this.requiereSeleccion = data.pagointentos || [];
          this.selected = [];
          this.$toastr.w(
            data.message ||
              "El pago tiene más de un pagointento cobrado. Elegí uno."
          );
        } else if (data && data.status === "ok") {
          this.lastResult = data;
          this.requiereSeleccion = null;
          this.$toastr.s(
            data.message || `Devolución OK · canal: ${data.canal}`
          );
        } else {
          this.$toastr.i("Respuesta recibida");
        }
      } catch (e) {
        const detail =
          (e && e.response && e.response.data && e.response.data.error) ||
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          "Error al comunicarse con el servicio";
        this.errorMsg = detail;
        this.$toastr.e(detail);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.devolucion-legend {
  font-size: 13px;
  line-height: 1.45;
}
.intentos-table >>> tbody tr {
  cursor: pointer;
}
.success-card {
  background: linear-gradient(180deg, #f1f8f4 0%, #ffffff 100%);
  border: 1px solid #c8e6c9;
  border-radius: 12px;
}
.success-avatar {
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.35);
}
.success-title {
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #2e7d32;
  margin: 0;
}
.success-subtitle {
  color: #555;
  font-size: 14px;
  margin: 0;
}
.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px 16px;
}
.result-item {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 12px;
}
.result-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #777;
  margin-bottom: 4px;
}
.result-value {
  font-size: 14px;
  color: #222;
  word-break: break-word;
}
.result-value--lg {
  font-size: 18px;
  font-weight: 700;
  color: #2e7d32;
}
</style>
